import { FloatingNode, useFloatingNodeId } from '@floating-ui/react';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { sizes } from '#@/shared/utils/styles';
import { zIndexValues } from '#@/shared/utils/styles/zIndexValues';

import type { DropdownActionProps } from '../DropdownAction';
import DropdownCard from '../DropdownCard';
import DropdownCardContent from '../DropdownCardContent';
import { Action } from './Action';
import { DropdownMenuContext } from './DropdownMenuContext';

export interface SubMenuProps extends Partial<DropdownActionProps> {
  /**
   * Label text for the submenu trigger item
   */
  label: React.ReactNode;
  /**
   * The submenu items
   */
  children: React.ReactNode;
}

const SUBMENU_OPEN_DELAY = 200; // ms
const SUBMENU_CLOSE_DELAY = 0; // ms

const SUBMENU_HORIZONTAL_OFFSET = 1; // px

const MAX_SUBMENU_LEVELS = 10;

/**
 * SubMenu component for nested dropdown menus
 */
export const SubMenu: React.FC<SubMenuProps> = ({ label, children, ...actionProps }) => {
  const { isTriggered, closeModal, currentNestingLevel } = useContext(DropdownMenuContext);

  const [isOpen, setIsOpen] = useState(false);
  const nodeId = useFloatingNodeId();

  // Calculate the next nesting level for children
  const childNestingLevel = currentNestingLevel + 1;

  // Get the appropriate z-index based on submenu level
  const getSubmenuLevelZIndex = (level: number) => {
    // Cap the level to the maximum supported submenu level
    const safeLevel = Math.min(level, MAX_SUBMENU_LEVELS);

    // Use the corresponding submenu level z-index
    // For level 1, use submenuLevel1, etc.
    return zIndexValues[`submenuLevel${safeLevel}` as keyof typeof zIndexValues];
  };

  // Get z-index for this submenu level
  const submenuZIndex = getSubmenuLevelZIndex(childNestingLevel);

  const childContextValue = useMemo(
    () => ({
      isTriggered,
      closeModal,
      currentNestingLevel: childNestingLevel,
    }),
    [isTriggered, closeModal, childNestingLevel],
  );

  // Close all submenus when the dropdown menu is closed
  useEffect(() => {
    if (!isTriggered) {
      setIsOpen(false);
    }
  }, [isTriggered]);

  return (
    <FloatingNode id={nodeId}>
      <DropdownCard
        isOpen={isOpen}
        placement="right-start"
        mainAxisOffset={sizes.dropdownCardContentPadding + SUBMENU_HORIZONTAL_OFFSET}
        crossAxisOffset={-sizes.dropdownCardContentPadding}
        onClose={() => setIsOpen(false)}
        zIndex={submenuZIndex}
        enableHoverInteraction
        openDelay={SUBMENU_OPEN_DELAY}
        closeDelay={SUBMENU_CLOSE_DELAY}
        onHoverStateChange={(open: boolean) => {
          setIsOpen(open);
        }}
        nodeId={nodeId}
        renderTrigger={({ ref, ...props }) => (
          <div ref={ref} {...props}>
            <Action
              iconRight={{ type: 'chevron-right' }}
              hasSubmenu
              hovered={isOpen}
              {...actionProps}
            >
              {label}
            </Action>
          </div>
        )}
      >
        <DropdownCardContent>
          <DropdownMenuContext.Provider value={childContextValue}>
            {children}
          </DropdownMenuContext.Provider>
        </DropdownCardContent>
      </DropdownCard>
    </FloatingNode>
  );
};

export default SubMenu;
